import { Inject, Injectable } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { delay, forkJoin, map, of } from "rxjs";
import { ApiSearch } from "src/app/api/services/api-utils.service";
import { FolderItem } from "src/app/api/services/folder";
import { FoldersApiService } from "src/app/api/services/folders-api.service";
import { UserStateService } from "src/app/auth/services/user-state.service";
import { FolderService, HasFolderStructure } from "src/app/shared/services/folder.service";
import { BatchActionHandlerService, ListOperation, ListOperations } from "./batch-action-handler.service";
import { ListAction, ListErrorHandlerService } from "./list-error-handler.service";
import { ListSearchStateService, SearchConfig } from "./list-search-state.service";
import { ListItem, ScrollingListStateService } from "./scrolling-list-state.service";

@Injectable()
export class PaginatedScrollingListService<T extends { folderId?: string; id?: string }> {
  list = toSignal(this.listState.list());
  activeOperation: ListOperation<T> | null;
  constructor(
    private readonly listState: ScrollingListStateService<T>,
    @Inject('WithListService')
    private readonly itemsService: WithList<T>,
    private readonly foldersService: FolderService,
    private readonly searchState: ListSearchStateService,
    @Inject('ListKind') kind: 'Display' | 'Schedule' | 'Presentation',
    private readonly listErrorHandler: ListErrorHandlerService,
    private readonly batchActionHandler: BatchActionHandlerService,
    private readonly userState: UserStateService,
    @Inject('HasFolderStructure')
    private readonly folderStructureService: HasFolderStructure<T>
  ) {
    this.searchState.init({
      folderKind: kind,
      query: undefined,
      includeSubcompanies: false,
      sortBy: 'changeDate',
      reverse: true,
      folderId: null,
      count: this.listState.maxCount
    });
    this.listState.setName(kind + 's');
    this.init();
  }

  private init() {
    this.searchState.searchChanged()
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (search) => {
          if (this.listState.isLoadingItems()) return;
          this.listState.clear();
          this.updateList(null, search);
        },
      });
  }

  load() {
    this.updateList(
      this.listState.currentCursor(),
      this.searchState.getSearchObject(),
    );
  }

  reload() {
    this.listState.clear();
    this.updateList(
      null,
      this.searchState.getSearchObject(),
    );
  }

  setOperations(listOperations: ListOperations<T>) {
    if (!listOperations || !listOperations.operations?.length) {
      return;
    }
    this.batchActionHandler.init(listOperations);

    listOperations.operations.forEach(operation => {
      this.updateSelectedAction(operation);
    });
  }

  private updateSelectedAction(operation: ListOperation<T>) {
    const execute = this.getExecuteAction(operation);

    operation.onClick = async (skipBeforeBatchAction, selection, args?) => {
      let selected = selection || this.listState.getSelected();

      if (!selected.length) {
        return;
      }

      if (operation.filter) {
        selected = operation.deepFilter
          ? this.filterSelected(
            await this.getItemsFromFolders(selected), operation.filter
          ) : this.filterSelected(selected, operation.filter);
      }
      let batchSelected: any[] = [...selected];

      if (operation.groupBy) {
        batchSelected = this.groupBySelected(batchSelected, operation.groupBy);
      }
      this.listState.clearMessages();
      operation.inProgress = true;
      this.batchActionHandler.finished().pipe(
        delay(2000) // Allow time for UI feedback before clearing operation state
      ).subscribe((finished) => {
        operation.inProgress = false;
        this.activeOperation = null;
        if (finished.success && operation.reloadList) {
          this.reload();
        } else if (finished.success === false) {
          this.listState.setError({
            apiError: this.listErrorHandler.processErrorCode(
              this.listState.getName(),
              operation.name.toLowerCase() as ListAction,
              finished.error
            ),
            errorMessage: finished.error?.message
          });
        }
      });
      if (operation.beforeBatchAction && !skipBeforeBatchAction) {
        this.listState.setLoadingItems(true);
        operation.beforeBatchAction(selected)
          .then(beforeArgs => {
            this.activeOperation = operation;
            return this.batchActionHandler.batch(batchSelected, execute, operation, beforeArgs);
          }).finally(() => this.listState.setLoadingItems(false));
      } else if (args) {
        this.activeOperation = operation;
        this.batchActionHandler.batch(batchSelected, execute, operation, args);
      } else { 
        this.activeOperation = operation;
        this.batchActionHandler.batch(batchSelected, execute, operation);
      }
    };
  }

  private groupBySelected(selected: ListItem<T>[], groupBy: ListOperation<T>['groupBy']) {
    if (typeof groupBy === 'boolean') {
      return [{items: selected.map(item => item.item)}];
    }

    if (typeof groupBy === 'string') {
      const grouped =selected.reduce((acc, item) => {
        const key = item.item[groupBy];
        if (!acc[key as string]) {
          acc[key as string] = [];
        }
        acc[key as string].push(item);
        return acc;
      }, {} as Record<keyof (T | FolderItem), ListItem<T>[]>);

      return Object.keys(grouped).map(key => ({
        [groupBy]: key,
        items: grouped[key] as ListItem<T>[]
      }));
    }
  }

  private filterSelected(selected: ListItem<T>[], filter: (item: ListItem<T>) => boolean) {
    return selected.filter(filter);
  }

  private getExecuteAction(operation: ListOperation<T>) {
    if (!operation.actionCall) {
      return null;
    }

    const originalAction = operation.actionCall;

    return (item: any, args: unknown) => {
      return originalAction(item, args)
        .then(() => {
          if (operation.isDelete) {
            this.listState.remove(item);
          }
        })
        .catch(e => {
          if (!this.listState.currentError()) {
            this.listState.setError({
              errorMessage: 'Something went wrong.',
              apiError: e?.message
            });
          }
          return Promise.reject(e);
        });
    };
  }

  private updateList(
    cursor: string | null,
    search: SearchConfig,
  ) {
    if (
      this.listState.isLoadingItems()
      || this.listState.isEndOfList()
    ) return;
    this.listState.setLoadingItems(true);
    return forkJoin([
      !cursor && !this.userState.isAssignedPublisher()
        ? this.foldersService.loadFolders(
          search.folderKind,
          search
        )
        : of([]),
      this.itemsService.list(
        !this.userState.isAssignedPublisher()
          ? search 
          : this.getSearchForAssignedPublisher(search),
        String(cursor)
      )
    ]).pipe(
      map(([folders, result]) => {
        return {
          folders: folders.filter(folder => this.foldersService.showFolderItem(
            search.folderId
            || FoldersApiService.ROOT_FOLDER_ID,
            folder.parentId
          ) || (!search.folderId && search.query)),
          items: this.userState.isAssignedPublisher()
            ? result.items 
            : result.items.filter(item => this.foldersService.showFolderItem(
              search.folderId
              || FoldersApiService.ROOT_FOLDER_ID,
              item.folderId
            ) || (!search.folderId && search.query)),
          cursor: result.cursor,
          nextPageToken: result.nextPageToken
        };
      })
    ).subscribe({
      next: ({ folders, items, cursor, nextPageToken }) => {
        this.listState.add(
          [...folders, ...items].map(item => this.mapItem(item as T | FolderItem)),
          cursor || nextPageToken);
      },
      error: (e) => {
        this.listState.setError({
          apiError: this.listErrorHandler.processErrorCode(
            this.listState.getName(),
            'load',
            e
          ),
          errorMessage: 'Failed to load ' + this.listState.getName() + '.'
        });
      },
      complete: () => this.listState.setLoadingItems(false)
    });
  }

  private async getItemsFromFolders(listItems: ListItem<T>[]) {
    const folders = listItems.filter(item => item.isFolder);
    const items = listItems.filter(item => !item.isFolder).map(item => item.item as T);
    this.listState.setLoadingItems(true);
    const subfolders = await Promise.all(
      folders.map(folder => this.foldersService.getItemsFromFolderAndSubfolders(
        folder.item.id, this.folderStructureService)
      )
    );
    this.listState.setLoadingItems(false);
    return [...items, ...subfolders.flat()].map(
      item => this.mapItem(item as T | FolderItem)
    );
  }

  private mapItem(item: T | FolderItem) {
    return {
      item: item,
      isFolder: !!(item as FolderItem).isFolder,
      selected: false
    } as ListItem<T>;
  }

  private getSearchForAssignedPublisher(search: SearchConfig) {
    const {folderId, ...rest} = search;
    return rest;
  }
}

export interface WithList<T> {
  list(apiSearch: ApiSearch, cursor?: string): Promise<{
    items: T[],
    cursor: string | null,
    nextPageToken: string | null 
  }>;
}
